import { groupBy, mapValues, maxBy, isNil } from 'lodash-es'
import {
  OrderDetail,
  NegotiationDetail,
  OrderStatus,
  Direction,
  NegotiationStatus,
  DealDetail,
  DealStatus,
  Currency,
  AuditDetail,
  DirectionFormat
} from 'api'
import { Decimal } from 'utils'
import moment from 'moment'
import { getNegotiatedValue, isAuditDetail } from 'view/routes/requests/shared/utils'
import { Locale, Translation } from 'locale'

export interface CashAndNotionalAmount {
  currency: Currency
  cashBuyCount: number
  cashBuyAmount: number
  cashSellCount: number
  cashSellAmount: number
  notionalBuyCount: number
  notionalBuyAmount: number
  notionalSellCount: number
  notionalSellAmount: number
}
export interface CashAmount {
  currency: Currency
  buyCount: number
  buyAmount: number
  sellCount: number
  sellAmount: number
}

export enum TradeAwayEnum {
  TRADED_AWAY = 'TRADED_AWAY'
}

export type NegotiationsByStatus = Record<keyof typeof NegotiationStatus | TradeAwayEnum, number>

export const getOrderCashAmounts = (orderDetails: OrderDetail[]): CashAmount[] => {
  const filteredOrderDetails = orderDetails.filter(
    orderDetail =>
      orderDetail.order.status !== OrderStatus.INCOMPLETE &&
      (orderDetail.collateral || orderDetail.collateralBasket) &&
      orderDetail.order.startConsideration
  )
  const ordersByCurrency = groupBy(
    filteredOrderDetails,
    orderDetail => orderDetail.collateral?.currency ?? orderDetail.productSector?.basketCurrency!
  )
  return Object.values(ordersByCurrency).map(currencyOrderDetails => {
    const buyOrderDetails = currencyOrderDetails.filter(orderDetail => orderDetail.order.direction === Direction.BUY)
    const sellOrderDetails = currencyOrderDetails.filter(orderDetail => orderDetail.order.direction === Direction.SELL)
    return {
      currency: currencyOrderDetails[0].collateral?.currency ?? currencyOrderDetails[0].productSector?.basketCurrency!,
      buyCount: buyOrderDetails.length,
      buyAmount: Decimal.sum(buyOrderDetails.map(orderDetail => orderDetail.order.startConsideration || 0)),
      sellCount: sellOrderDetails.length,
      sellAmount: Decimal.sum(sellOrderDetails.map(orderDetail => orderDetail.order.startConsideration || 0))
    }
  })
}

const getStartConsideration = (detail: NegotiationDetail | AuditDetail) =>
  isAuditDetail(detail) ? detail.negotiation.startConsideration : getNegotiatedValue(detail.negotiation.startConsideration)

export const getNegotiationCashAmounts = (negotiationDetails: (NegotiationDetail | AuditDetail)[]): CashAmount[] => {
  const filteredNegotiationDetails = negotiationDetails.filter(
    negotiationDetail => negotiationDetail.collateral && getStartConsideration(negotiationDetail)
  )

  const negotiationDetailsByRfq = groupBy(filteredNegotiationDetails, negotiationDetail => negotiationDetail.negotiation.parentOrderId)
  const maximumNegotiationDetailByRfq = mapValues(
    negotiationDetailsByRfq,
    rfqNegotiationDetails =>
      maxBy(rfqNegotiationDetails, negotiationDetail => getStartConsideration(negotiationDetail)) as NegotiationDetail
  )
  const maximumNegotiationDetails = Object.values(maximumNegotiationDetailByRfq)
  const negotiationsByCurrency = groupBy(maximumNegotiationDetails, negotiationDetail => negotiationDetail.collateral!.currency)
  return Object.values(negotiationsByCurrency).map(currencyNegotiationDetails => {
    const ownOrder = currencyNegotiationDetails[0].ownOrder
    const buyNegotiationDetails = currencyNegotiationDetails.filter(negotiationDetail =>
      ownOrder ? negotiationDetail.negotiation.direction === Direction.BUY : negotiationDetail.negotiation.direction === Direction.SELL
    )
    const sellNegotiationDetails = currencyNegotiationDetails.filter(negotiationDetail =>
      ownOrder ? negotiationDetail.negotiation.direction === Direction.SELL : negotiationDetail.negotiation.direction === Direction.BUY
    )
    return {
      currency: currencyNegotiationDetails[0].collateral!.currency,
      buyCount: buyNegotiationDetails.length,
      buyAmount: Decimal.sum(buyNegotiationDetails.map(negotiationDetail => getStartConsideration(negotiationDetail) || 0)),
      sellCount: sellNegotiationDetails.length,
      sellAmount: Decimal.sum(sellNegotiationDetails.map(negotiationDetail => getStartConsideration(negotiationDetail) || 0))
    }
  })
}

export const getNegotiationCashAmountsForStatus = (
  negotiationDetails: (NegotiationDetail | AuditDetail)[],
  status: NegotiationStatus
): CashAmount[] =>
  getNegotiationCashAmounts(
    negotiationDetails.filter((negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.status === status)
  )

export const getNegotiationByStatus = (completedNegotiations: (NegotiationDetail | AuditDetail)[], isSellSideOrganisation: boolean) =>
  completedNegotiations.reduce((acc: NegotiationsByStatus, negotiationDetail: NegotiationDetail) => {
    // FW BQ-1553 Fix to display correct status text based on lastResponder being present.
    // This assumes that lastResponder will not be present unless the Negotiation has been at status QUOTED
    // A proper fix to add a new status will be done on the server at some point in the future

    let key: NegotiationStatus | TradeAwayEnum = negotiationDetail.negotiation.status

    if (negotiationDetail.negotiation.status === NegotiationStatus.MISSED) {
      if (isSellSideOrganisation) {
        key = negotiationDetail.negotiation.lastResponderId ? TradeAwayEnum.TRADED_AWAY : NegotiationStatus.MISSED
      } else {
        key = negotiationDetail.negotiation.lastResponderId ? NegotiationStatus.REJECTED : NegotiationStatus.CANCELLED
      }
    }

    acc[key] = acc[key] ? acc[key] + 1 : 1
    return acc
  }, {} as NegotiationsByStatus)

export const getDealCashAmounts = (dealDetails: DealDetail[]): [CashAmount, number][] => {
  const filteredDealDetails = dealDetails.filter(dealDetail => dealDetail.deal.status !== DealStatus.CANCELLED && dealDetail.collateral)
  const dealsByCurrency = groupBy(filteredDealDetails, dealDetail => dealDetail.collateral!.currency)

  const notionalTimesDays = (dealDetail: DealDetail) => {
    if (isNil(dealDetail.deal.endDate) || isNil(dealDetail.deal.startDate)) {
      return 0
    }

    return Decimal.multiply(dealDetail.deal.notional, moment(dealDetail.deal.endDate).diff(moment(dealDetail.deal.startDate), 'days'))
  }

  return Object.values(dealsByCurrency).map(currencyDealDetails => {
    const buyDealDetails = currencyDealDetails.filter(dealDetail => dealDetail.deal.direction === Direction.BUY)
    const sellDealDetails = currencyDealDetails.filter(dealDetail => dealDetail.deal.direction === Direction.SELL)
    const currency = currencyDealDetails[0].collateral!.currency
    return [
      {
        currency: currency,
        buyCount: buyDealDetails.length,
        buyAmount: Decimal.sum(buyDealDetails.map(notionalTimesDays)),
        sellCount: sellDealDetails.length,
        sellAmount: Decimal.sum(sellDealDetails.map(notionalTimesDays))
      },
      Decimal.sum([
        ...buyDealDetails.map(dealDetail => dealDetail.deal.notional),
        ...sellDealDetails.map(dealDetail => dealDetail.deal.notional)
      ])
    ]
  })
}

export function getDirectionText(directionFormat: DirectionFormat): [string, string, number] {
  switch (directionFormat) {
    case DirectionFormat.RVP_RP:
      return [Locale.translate(Translation.TC_REV_REPO), Locale.translate(Translation.TC_REPO), 70]

    case DirectionFormat.BUY_SELL:
      return [Locale.translate(Translation.TC_BUY), Locale.translate(Translation.TC_SELL), 55]

    case DirectionFormat.BID_OFFER:
      return [Locale.translate(Translation.TC_BID), Locale.translate(Translation.TC_OFFER), 60]

    case DirectionFormat.BORROW_LEND:
      return [Locale.translate(Translation.TC_BORROW), Locale.translate(Translation.TC_LEND), 70]

    default:
      return [Locale.translate(Translation.TC_REV_REPO), Locale.translate(Translation.TC_REPO), 70]
  }
}

export const getOpenRepoCashAndNotionalAmounts = (dealDetails: DealDetail[]): CashAndNotionalAmount[] => {
  const dealsByCurrency = groupBy(dealDetails, dealDetail => dealDetail.deal.currency)

  return Object.values(dealsByCurrency).map(currencyDealDetails => {
    const ownOrder = currencyDealDetails[0].ownOrder
    const buyDealDetails = currencyDealDetails.filter(dealDetail =>
      ownOrder ? dealDetail.deal.direction === Direction.BUY : dealDetail.deal.direction === Direction.SELL
    )
    const sellDealDetails = currencyDealDetails.filter(dealDetail =>
      ownOrder ? dealDetail.deal.direction === Direction.SELL : dealDetail.deal.direction === Direction.BUY
    )
    return {
      currency: currencyDealDetails[0].deal.currency,
      cashBuyCount: buyDealDetails.length,
      cashBuyAmount: Decimal.sum(buyDealDetails.map(dealDetail => dealDetail.deal.startConsideration)),
      cashSellCount: sellDealDetails.length,
      cashSellAmount: Decimal.sum(sellDealDetails.map(dealDetail => dealDetail.deal.startConsideration)),
      notionalBuyCount: buyDealDetails.length,
      notionalBuyAmount: Decimal.sum(buyDealDetails.map(dealDetail => dealDetail.deal.notional)),
      notionalSellCount: sellDealDetails.length,
      notionalSellAmount: Decimal.sum(sellDealDetails.map(dealDetail => dealDetail.deal.notional))
    }
  })
}
